import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LTabStrip, LFade, LDialog } from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAsterisk, faCheck } from "@fortawesome/sharp-solid-svg-icons";
import { AuthContext } from "../../../../../context/AuthContext";
import { useNewClientWizardContext } from "./Context/NewClientWizardContext";
import { DatabaseConfigStep } from "./Steps/DatabaseConfigStep";
import { FirmProfileStep } from "./Steps/FirmProfileStep";
import { CatalogsStep } from "./Steps/CatalogsStep";
import { ReviewStep } from "./Steps/ReviewStep";
import { validateFirmProfile, validateProductConfig } from "./utils/validations";
import {
  ProvisionClientProductsResult,
  WizardClient,
  WizardProduct,
  emptyProductConfig,
  suggestDatabaseCatalog,
} from "./types";
import styles from "./NewClientWizard.module.css";

interface NewClientWizardModalProps {
  isOpen: boolean;
  client: WizardClient;
  product: WizardProduct;
  onClose: () => void;
  /** Called after a successful provisioning run so the product list can refresh. */
  onProvisioned: () => void;
}

interface Tab {
  title: string | ReactNode;
  body: ReactNode;
}

/** Provisioning wizard for one client adopting one PBIN product. Launched from the
 * Client-Products list; clients and users are managed by the Portal's normal flows. */
export function NewClientWizardModal({ isOpen, client, product, onClose, onProvisioned }: NewClientWizardModalProps) {
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [provisioning, setProvisioning] = useState(false);
  const [result, setResult] = useState<ProvisionClientProductsResult | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const { formData, updateFormData, resetFormData, error, setError } = useNewClientWizardContext();
  const authContext = useContext(AuthContext) as any;
  const token: string = authContext.authState().accessToken.accessToken;

  // Seed the config with the conventional database name for this client + product side.
  useEffect(() => {
    if (isOpen) {
      updateFormData({
        config: emptyProductConfig(suggestDatabaseCatalog(product.productTypeId, client.name)),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, client.clientGuid, product.productGuid]);

  const handleProvision = useCallback(async () => {
    setProvisioning(true);
    setError(null);
    setTimedOut(false);
    try {
      // Resolve the current portal user for the CreatedBy audit fields.
      const meResponse = await fetch(
        `${process.env.REACT_APP_API_GATEWAY_BASE}/multitenancy/api/users/ByOktaId`,
        { headers: { Authorization: `Bearer ${token}`, Accept: "application/json" }, mode: "cors" }
      );
      if (!meResponse.ok) throw new Error("Could not resolve the current portal user.");
      const me = await meResponse.json();

      const config = formData.config;
      const payload = {
        clientGuid: client.clientGuid,
        createdBy: me.userGuid,
        products: [
          {
            productGuid: product.productGuid,
            databaseServer: config.databaseServer,
            databaseCatalog: config.databaseCatalog,
            elasticPoolName: config.elasticPoolName,
            databaseUser: config.databaseUser,
            databasePassword: config.databasePassword,
            authDatabaseServer: config.authDatabaseServer,
            authDatabaseCatalog: config.authDatabaseCatalog,
            authDatabaseUser: config.authDatabaseUser,
            authDatabasePassword: config.authDatabasePassword,
            storagePath: config.storagePath,
            storageServerUserName: config.storageServerUserName,
            storageServerUserPassword: config.storageServerUserPassword,
            storageTypeId: config.storageTypeId,
            incomingHandlerEmailServer: config.incomingHandlerEmailServer,
            incomingHandlerEmailUser: config.incomingHandlerEmailUser,
            incomingHandlerEmailPassword: config.incomingHandlerEmailPassword,
            incomingHandlerConnectionPort: config.incomingHandlerConnectionPort,
            provisioningSqlUser: config.provisioningSqlUser,
            provisioningSqlPassword: config.provisioningSqlPassword,
            gabFlavor: config.gabFlavor,
            firmProfile: formData.firmProfile,
            associatedDtcs: formData.associatedDtcs,
            accountTypes: formData.accountTypes,
          },
        ],
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_GATEWAY_BASE}/multitenancy/api/Provisioning/Client/${client.clientGuid}/Products`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          mode: "cors",
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        if (response.status === 503 || response.status === 504) {
          setTimedOut(true);
          return;
        }
        const detail = await response.text();
        throw new Error(detail || `Provisioning request failed (${response.status}).`);
      }

      const provisionResult: ProvisionClientProductsResult = await response.json();
      setResult(provisionResult);
      if (provisionResult.success) onProvisioned();
    } catch (err: any) {
      setError("Provisioning failed: " + err.message);
    } finally {
      setProvisioning(false);
    }
  }, [formData, client, product, token, setError, onProvisioned]);

  const handleClose = () => {
    resetFormData();
    setResult(null);
    setTimedOut(false);
    setSelectedTab(0);
    onClose();
  };

  const getTabTitle = useCallback(
    (baseTitle: string, isComplete: boolean) => (
      <span className={styles.tabTitle}>
        {baseTitle}{" "}
        {isComplete ? (
          <FontAwesomeIcon icon={faCheck} color="green" fontSize={8} />
        ) : (
          <FontAwesomeIcon icon={faAsterisk} color="red" fontSize={6} />
        )}
      </span>
    ),
    []
  );

  const isConfigComplete = useMemo(() => validateProductConfig(formData), [formData]);
  const isFirmComplete = useMemo(() => validateFirmProfile(formData), [formData]);

  const tabs: Tab[] = useMemo(
    () => [
      {
        title: getTabTitle("Database & Storage", isConfigComplete),
        body: <DatabaseConfigStep productName={product.name} />,
      },
      {
        title: getTabTitle("Firm Profile", isFirmComplete),
        body: <FirmProfileStep clientName={client.name} />,
      },
      { title: "Account Types & DTCs", body: <CatalogsStep /> },
      {
        title: <b>Review & Provision</b>,
        body: (
          <ReviewStep
            client={client}
            product={product}
            onProvision={handleProvision}
            provisioning={provisioning}
            result={result}
          />
        ),
      },
    ],
    [
      getTabTitle,
      isConfigComplete,
      isFirmComplete,
      handleProvision,
      provisioning,
      result,
      client,
      product,
    ]
  );

  if (!isOpen) return null;

  const modalBody = (
    <div className={styles.wizardContent}>
      {error && <div className={styles.error}>{error}</div>}
      {timedOut && (
        <div className={styles.notice}>
          <strong>Provisioning is still finishing — this is not a failure.</strong> The request
          timed out at the gateway (~90s), but creating the database can take several minutes and
          usually completes on the server. Don't re-run the wizard. Close this and open the
          client's product list: if the product now shows the enable/disable toggle, provisioning
          finished. If it still shows the <em>Provision</em> button, it did not complete — drop the
          partially-created database before retrying.
        </div>
      )}
      <LTabStrip
        selected={selectedTab}
        onSelect={(e: { selected: number }) => setSelectedTab(e.selected)}
        orientation="vertical"
        tabs={tabs}
        tabPosition="top"
        key={selectedTab}
      />
    </div>
  );

  return (
    <LFade transitionEnterDuration={300}>
      <LDialog
        width="1050"
        title={`Provision ${product.name} for ${client.name}`}
        subtitle={<div className={styles.modalSubtitle}>* Required</div>}
        body={modalBody}
        actionLayout="end"
        actionCloseButton={handleClose}
        actionOverlay={handleClose}
      />
    </LFade>
  );
}
